'use client';

import React, { useEffect } from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import { usePromocode } from '../model/hooks';
import { useSearchParams } from 'next/navigation';
import { useBonuses } from '@/features/profile/model/hooks';
import { driver } from 'driver.js';
import { validateSchema } from '@/shared/libs/validate';
import { useRouter } from 'next-nprogress-bar';
import { useScopedI18n, useCurrentLocale } from '@locales/client';

import { Box } from '@/shared/ui/box';
import { FormField } from '@/shared/ui/form/form-field';
import { Field, Form } from 'react-final-form';
import { Condition } from '@/shared/ui/condition';
import {
  IconPencilWrapper,
  FormIconButton,
} from '@/features/profile/ui/styles';
import { Image } from '@/shared/ui/image';
import { MediumHeadingQuinary } from '@/shared/ui/typography';

const promoCodeSchema = (errorLoc: any) =>
  z.object({
    code: z
      .string({ required_error: errorLoc('error_required') })
      .min(5, { message: errorLoc('error_min') })
      .regex(/^[A-Z0-9]+$/, { message: errorLoc('error_invalid') })
      .transform((val) => val.toUpperCase()),
  });

export const validate = (errorLoc: any) =>
  validateSchema(promoCodeSchema(errorLoc));

const Promocodes = ({ mobileLandscape }) => {
  const { data, isLoading, activate, mutateBonuses, deactivate } = useBonuses();
  const [isEditCode, setIsEditCode] = React.useState(false);
  const { trigger: promocodeTrigger } = usePromocode();
  const [form, setForm] = React.useState({ code: '' });
  const router = useRouter();
  const locale = useCurrentLocale();
  const searchParams = useSearchParams();
  const t = useScopedI18n('bonus');
  const errorLoc = useScopedI18n('bonus.promocode');

  useEffect(() => {
    if (searchParams.has('promo') && !isLoading) {
      setForm((prev) => ({
        ...prev,
        code: searchParams.get('promo'),
      }));
      setIsEditCode(true);
      const driverObj = driver({ popoverClass: 'my-custom-popover-class' });
      driverObj.highlight({
        element: '#promo-form',
        popover: {
          title: t('onboarding.title'),
          description: t('onboarding.desc'),
        },
      });
    }
  }, [searchParams, isLoading]);

  const onPromoUpdate = async (values) => {
    try {
      await promocodeTrigger(values.code);
      await mutateBonuses();
      setIsEditCode(false);
      router.push(`/${locale}/profile/bonuses`);
    } catch (e) {
      console.error(e);
      setIsEditCode(false);
    }
    setForm({ code: '' });
  };

  const handleInputChange = (value: string) => {
    setIsEditCode(!!value);
  };

  if (isLoading) return null;

  const validation = validate(errorLoc);

  return (
    <Box
      $display="flex"
      $flexDirection="column"
      $position="relative"
      $justifyContent="flex-start"
      $alignItems="center"
      $gap={['60px', '24px']}
      $marginTop="30px"
      $padding="0 18px"
      $height={['420px', '100%']}
      $width="100%"
    >
      <Background isEditCode={isEditCode} />
      <Content isEditCode={isEditCode}>
        <Form
          onSubmit={onPromoUpdate}
          validate={validation}
          initialValues={form}
          render={({ handleSubmit }) => (
            <WraperForm>
              <MediumHeadingQuinary style={{ marginBottom: '18px' }}>
                {t('promocode.placeholder')}
              </MediumHeadingQuinary>

              <form onSubmit={handleSubmit}>
                <Box
                  $width={[264, '100%']}
                  id="promo-form"
                  $marginTop={[0, 16]}
                  $position="relative"
                  $display="flex"
                >
                  <Field
                    name="code"
                    component={FormField}
                    type="text"
                    autoComplete="off"
                    placeholder={t('promocode.placeholder')}
                    onInputChange={handleInputChange}
                    bgColor="rgba(39, 38, 43, 0.60)"
                    placeholderColor="#FFF"
                    errorColor="#FFF"
                  />

                  <Condition match={isEditCode}>
                    <Box $display="flex" $gap={6} $marginLeft={4} $height={48}>
                      <FormIconButton type="submit" variant="orangeGradient">
                        <Image
                          src="/images/icons/accept-success-white.svg"
                          width={24}
                          height={24}
                        />
                      </FormIconButton>
                    </Box>
                  </Condition>
                </Box>
              </form>
            </WraperForm>
          )}
        />
      </Content>
    </Box>
  );
};

const PromocodesWithBreakpointsSlice = withBreakpointsSlice(
  Promocodes,
  ({ mobileLandscape }) => ({ mobileLandscape })
);

export { PromocodesWithBreakpointsSlice as Promocodes };

const WraperForm = styled.div`
  border-radius: 18px;
  background: rgba(67, 21, 20, 0.6);
  backdrop-filter: blur(6px);
  padding: 24px;
  margin: auto;
  text-align: center;
`;

const Background = styled.div<{ isEditCode: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  background-image:
    linear-gradient(0deg, #131315 0%, rgba(19, 19, 21, 0) 100%),
    url('/images/background/promocode_bg.png');
  background-size: auto;
  background-repeat: no-repeat;
  background-position: center;

  @media (max-width: 768px) {
    width: 100vw;
    height: 100%;
    background-position: bottom 50%;
    background-size: cover;
    background-image:
      linear-gradient(0deg, #131315 0%, rgba(19, 19, 21, 0) 100%),
      url('/images/background/promocode_bg_mob.png');
  }
`;

const Content = styled.div<{ isEditCode: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  min-width: 284px;
  min-height: 419px;
  align-items: center;

  @media (max-width: 768px) {
    align-items: stretch;
  }
`;
